import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, Sparkles, Leaf, HeartHandshake, Flower2, Scissors, CalendarDays, Droplets, Waves, Wind } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, assetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PractitionerCard } from "@/components/PractitionerCard";
import { ReviewHighlights } from "@/components/ReviewHighlights";
import { ModalitySpotlight } from "@/components/ModalitySpotlight";
import { Enso } from "@/components/Layout";
import { TipJar } from "@/components/TipJar";
import { SEO } from "@/components/SEO";

const icons = { sparkles: Sparkles, leaf: Leaf, "heart-handshake": HeartHandshake, "flower-2": Flower2, scissors: Scissors, "calendar-days": CalendarDays };
const badgeIcons = { "First Ripple": Droplets, "Wave Maker": Waves, "Tsunami": Wind };

export default function Home() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [learn, setLearn] = useState([]);
  const [posts, setPosts] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [search, setSearch] = useState("");
  const [loc, setLoc] = useState("all");
  const [email, setEmail] = useState("");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/practitioners/locations").then((r) => {
      const flat = r.data.flatMap((l) => l.cities.map((c) => ({ city: c, country: l.country })));
      setLocations(flat);
    });
    api.get("/practitioners", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 3)));
    api.get("/learn").then((r) => setLearn(r.data.slice(0, 4)));
    api.get("/corner").then((r) => setPosts(r.data.slice(0, 3)));
    api.get("/referrals/leaderboard").then((r) => setLeaders(r.data));
  }, []);

  const goSearch = () => {
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (loc && loc !== "all") {
      const [c, co] = loc.split("::");
      q.set("city", c);
      q.set("country", co);
    }
    navigate(`/browse?${q.toString()}`);
  };

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/emails/capture", { email, source: "homepage" });
      toast.success("You're on the list. Welcome to the Ripple.");
      setEmail("");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <SEO description="O.R.E.C — a vetted community of spiritual, wellness, and values-driven practitioners in Calgary. Browse, learn, and book with people you can trust." />
      <section className="silk-hero hero-scene" data-testid="home-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 pb-20 sm:pt-14 sm:pb-28 relative z-10 grid lg:grid-cols-[minmax(320px,520px)_1fr] gap-6 lg:gap-14 items-center">
          <div className="relative flex items-center justify-center lg:justify-start fade-up" data-testid="hero-logo-scene">
            <img src="/orec-logo-full.png" alt="O.R.E.C — Our Ripple Effect Community" className="hero-logo" data-testid="hero-logo" />
          </div>
          <div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-foreground max-w-3xl leading-[1.12] fade-up" style={{ animationDelay: "0.08s" }}>
              Great people, doing great things <span className="font-sans font-light">-</span> <em>right</em> here.
            </h1>
            <p className="mt-6 max-w-xl text-base text-muted-foreground leading-relaxed fade-up" style={{ animationDelay: "0.16s" }}>
              A vetted community of spiritual, wellness, and values-driven practitioners. Every profile personally approved. Every booking a Ripple.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 max-w-2xl fade-up" style={{ animationDelay: "0.24s" }}>
              <div className="relative flex-1">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="home-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && goSearch()}
                  placeholder="Reiki, tarot, massage, coaching…" className="pl-11 h-12 bg-card/70 border-border rounded-full" />
              </div>
              <Select value={loc} onValueChange={setLoc}>
                <SelectTrigger data-testid="home-city-select" className="h-12 sm:w-52 rounded-full bg-card/70">
                  <SelectValue placeholder="Anywhere" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Anywhere</SelectItem>
                  {locations.map((l) => (
                    <SelectItem key={`${l.city}::${l.country}`} value={`${l.city}::${l.country}`}>{l.city}, {l.country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button data-testid="home-search-btn" onClick={goSearch} className="h-12 rounded-full px-8">Search</Button>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border/60 bg-card/20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 flex flex-col sm:flex-row gap-8 sm:gap-12 items-start" data-testid="bridgett-welcome">
          <div className="shrink-0 flex flex-col items-center gap-3">
            <Enso size={72} />
            <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Founder</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">A welcome from The Team</p>
            <p className="font-display text-xl sm:text-2xl text-foreground leading-relaxed mb-5">
              "Welcome, friend. O.R.E.C began with a simple belief — the best healers and helpers shouldn't need big ad budgets to be found, just a community that vouches for them. Every practitioner here is someone we'd send our own family to. Take your time, look around, and when you're ready, book a session. That's how Ripples begin."
            </p>
            <p className="text-sm text-primary mb-7">— The Team at Our Ripple Effect Community</p>
            <div className="flex flex-wrap items-center gap-4">
              <Button variant="outline" data-testid="welcome-corner-btn" className="rounded-full px-6" onClick={() => navigate("/corner")}>
                Visit Bridgett's Corner <ArrowRight size={14} className="ml-2" />
              </Button>
              <TipJar />
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-display text-lg md:text-2xl text-foreground">Browse by category</h2>
          <Link to="/browse" className="cream-link text-sm flex items-center gap-1">All practitioners <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {categories.map((c) => {
            const Icon = icons[c.icon] || Sparkles;
            return (
              <button key={c.id} data-testid={`category-tile-${c.slug}`}
                onClick={() => navigate(`/browse?category=${c.id}`)}
                className="silk-card rounded-lg p-6 text-left">
                <Icon size={22} className="text-primary mb-4" />
                <p className="font-display text-lg text-foreground">{c.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{c.subcategories?.length || 0} specialties</p>
              </button>
            );
          })}
        </div>
      </section>

      <ModalitySpotlight />

      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
          <h2 className="font-display text-lg md:text-2xl text-foreground mb-10">Featured practitioners</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-practitioners">
            {featured.map((p) => <PractitionerCard key={p.id} p={p} />)}
          </div>
        </section>
      )}

      <ReviewHighlights />

      {leaders.length > 0 && (
        <section className="border-y border-border/60 bg-card/30">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">The Ripple Makers</p>
              <h2 className="font-display text-lg md:text-2xl text-foreground mb-4">Every invite starts a wave</h2>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-md mb-8">
                These members grew our community by sharing their personal Ripple link. Invite a friend, earn badges, and watch your Ripple effect spread.
              </p>
              <Button data-testid="leaderboard-cta" className="rounded-full px-7" onClick={() => navigate("/ripples")}>
                Start your Ripple <ArrowRight size={14} className="ml-2" />
              </Button>
            </div>
            <div className="space-y-2" data-testid="ripple-leaderboard">
              {leaders.map((l, i) => {
                const BIcon = badgeIcons[l.badge];
                return (
                  <div key={i} className="silk-card rounded-lg px-5 py-3.5 flex items-center gap-4" data-testid={`leaderboard-row-${i + 1}`}>
                    <span className={`font-display text-xl w-7 text-center shrink-0 ${i === 0 ? "text-primary" : "text-muted-foreground"}`}>{i + 1}</span>
                    <p className="text-sm text-foreground flex-1 truncate">{l.name}</p>
                    {l.badge && BIcon && (
                      <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-primary border border-primary/30 rounded-full px-3 py-1">
                        <BIcon size={11} /> {l.badge}
                      </span>
                    )}
                    <span className="text-sm text-muted-foreground shrink-0 flex items-center gap-1.5">
                      <Droplets size={13} className="text-primary" /> {l.count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-12">
        <div>
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-display text-lg md:text-2xl text-foreground">From the Learn Library</h2>
            <Link to="/learn" className="cream-link text-sm flex items-center gap-1">Explore <ArrowRight size={14} /></Link>
          </div>
          <div className="space-y-3">
            {learn.map((e) => (
              <Link key={e.id} to={`/learn/${e.slug}`} className="silk-card rounded-lg p-4 flex items-center justify-between gap-4 block">
                <div className="flex items-center gap-4 min-w-0">
                  {e.image && <img src={assetUrl(e.image)} alt="" loading="lazy" className="w-16 h-16 rounded-md object-cover shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{e.topic}</p>
                    <p className="font-display text-lg text-foreground truncate">{e.title}</p>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{e.summary}</p>
                  </div>
                </div>
                <ArrowRight size={16} className="text-muted-foreground shrink-0" />
              </Link>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-display text-lg md:text-2xl text-foreground">Bridgett's Corner</h2>
            <Link to="/corner" className="cream-link text-sm flex items-center gap-1">Read all <ArrowRight size={14} /></Link>
          </div>
          <div className="space-y-3">
            {posts.map((p) => (
              <Link key={p.id} to={`/corner/${p.slug}`} className="silk-card rounded-lg p-5 block">
                <p className="font-display text-lg text-foreground">{p.title}</p>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{p.summary}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 text-center">
          <h2 className="font-display text-lg md:text-2xl text-foreground mb-3">Join the ripple</h2>
          <p className="text-sm text-muted-foreground mb-8">Occasional notes from The Team — new practitioners, new posts, nothing noisy.</p>
          <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input data-testid="home-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com" className="h-11 rounded-full bg-card/70" />
            <Button data-testid="home-email-submit" type="submit" className="h-11 rounded-full px-7">Notify me</Button>
          </form>
        </div>
      </section>
    </div>
  );
}
